import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LoopVideoBlock from "../video/LoopVideoBlock";
import { useIsMobile } from "../../hooks/useIsMobile";
import GradientButton from "../patterns/motion/GradientButton";
import colors from "../../designTokens/colors.esm";
import { useTranslation } from "react-i18next";


// Firebase Storage에 업로드된 커뮤니티 배너 비디오 URL (최종 버전)
const COMMUNITY_BANNER_VIDEO_URL =
	"https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fcommunity%2Fcommunity_banner_final.mp4?alt=media";

// Discord SVG Icon (from Footer.js)
const DiscordIcon = ({ size = 24, color = "currentColor" }) => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 33 32"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		style={{ color }}
	>
		<g clipPath="url(#clip0_10031_177575)">
			<mask
				id="mask0_10031_177575"
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="0"
				y="4"
				width="33"
				height="25"
			>
				<path
					d="M32.5034 4.00195H0.503906V28.7758H32.5034V4.00195Z"
					fill="white"
				/>
			</mask>
			<g mask="url(#mask0_10031_177575)">
				<path
					d="M27.5928 6.20817C25.5533 5.27289 23.3662 4.58382 21.0794 4.18916C21.0378 4.18154 20.9962 4.20057 20.9747 4.23864C20.6935 4.73863 20.3819 5.3909 20.1637 5.90358C17.7042 5.53558 15.2573 5.53558 12.8481 5.90358C12.6299 5.37951 12.307 4.73863 12.0245 4.23864C12.003 4.20184 11.9614 4.18281 11.9198 4.18916C9.63431 4.58255 7.44721 5.27163 5.40641 6.20817C5.38874 6.21578 5.3736 6.22848 5.36355 6.24497C1.21508 12.439 0.078646 18.4809 0.636144 24.4478C0.638667 24.477 0.655064 24.5049 0.677768 24.5227C3.41481 26.5315 6.06609 27.7511 8.66815 28.5594C8.70979 28.5721 8.75392 28.5569 8.78042 28.5226C9.39594 27.6826 9.94461 26.7968 10.4151 25.8653C10.4428 25.8107 10.4163 25.746 10.3596 25.7244C9.48927 25.3945 8.66058 24.9922 7.86343 24.5354C7.80038 24.4986 7.79533 24.4084 7.85333 24.3653C8.02108 24.2397 8.18888 24.109 8.34906 23.977C8.37804 23.9529 8.41842 23.9478 8.45249 23.963C13.6894 26.3526 19.359 26.3526 24.5341 23.963C24.5682 23.9465 24.6086 23.9516 24.6388 23.9757C24.799 24.1077 24.9668 24.2397 25.1358 24.3653C25.1938 24.4084 25.19 24.4986 25.127 24.5354C24.3298 25.0011 23.5011 25.3945 22.6296 25.7232C22.5728 25.7447 22.5476 25.8107 22.5754 25.8653C23.0559 26.7955 23.6046 27.6812 24.2087 28.5213C24.234 28.5569 24.2794 28.5721 24.321 28.5594C26.9357 27.7511 29.5869 26.5315 32.324 24.5227C32.348 24.5049 32.3631 24.4783 32.3656 24.4491C33.0328 17.5506 31.2481 11.5584 27.6344 6.24623C27.6256 6.22848 27.6105 6.21578 27.5928 6.20817ZM11.1971 20.8146C9.62043 20.8146 8.32129 19.3679 8.32129 17.5913C8.32129 15.8146 9.59523 14.368 11.1971 14.368C12.8115 14.368 14.0981 15.8273 14.0729 17.5913C14.0729 19.3679 12.7989 20.8146 11.1971 20.8146ZM21.8299 20.8146C20.2533 20.8146 18.9541 19.3679 18.9541 17.5913C18.9541 15.8146 20.228 14.368 21.8299 14.368C23.4444 14.368 24.7309 15.8273 24.7057 17.5913C24.7057 19.3679 23.4444 20.8146 21.8299 20.8146Z"
					fill="currentColor"
				/>
			</g>
		</g>
		<defs>
			<clipPath id="clip0_10031_177575">
				<rect
					width="31.9995"
					height="32"
					fill="white"
					transform="translate(0.5)"
				/>
			</clipPath>
		</defs>
	</svg>
);

/**
 * DiscordCommunityBanner - 디스코드 커뮤니티 초대 배너 컴포넌트
 * @param {object} props
 * @param {Function} [props.onJoinClick] - 참여하기 버튼 클릭 콜백
 * @param {string} [props.discordInviteUrl] - 디스코드 초대 URL (기본값: 임시 URL)
 */
const DiscordCommunityBanner = ({
	onJoinClick,
	discordInviteUrl = "https://discord.com/invite/JWjRJ9RYk4",
}) => {
	const theme = useTheme();
	const isMobile = useIsMobile("sm");
    const { t } = useTranslation();

	const handleJoinClick = () => {
		if (onJoinClick) {
			onJoinClick();
		} else {
			// 기본 동작: 새 탭에서 디스코드 링크 열기
			window.open(discordInviteUrl, "_blank");
		}
	};

	return (
		<Stack
			direction={isMobile ? "column" : "row"}
			sx={{
				width: "100%",
				borderRadius: "24px",
				overflow: "hidden",
				border: `0.5px solid ${theme.palette.text.primary}`,
				height: isMobile ? "auto" : "240px",
			}}
		>
			{/* 텍스트 영역 컨테이너 */}
			<Stack
				gap={4}
				sx={{
					flex: 1,
					p: isMobile ? 3 : 4,
					display: "flex",
					flexDirection: "column",
					justifyContent: "flex-start",
				}}
			>
				<Stack spacing={0.5}>
                    <Typography
						variant={isMobile ? "h6" : "h5"}
						fontWeight={800}
						color="text.primary"
					>
                        {t("discordBanner.title")}
					</Typography>
					<Typography
						variant="body1"
						color="text.secondary"
						sx={{
							maxWidth: isMobile ? "100%" : "400px",
							lineHeight: 1.6,
							mb: 1,
						}}
					>
                        {t("discordBanner.description")}
					</Typography>
				</Stack>
				
				{/* 모바일에서만 보이는 비디오 블록 */}
				{isMobile && (
					<Box
						sx={{
							width: "100%",
							height: "200px",
							flexShrink: 0,
						}}
					>
						<Box
							sx={{
								width: "100%",
								height: "100%",
								transform: "scale(1)",
								transformOrigin: "center",
							}}
						>
							<LoopVideoBlock
								videoUrl={COMMUNITY_BANNER_VIDEO_URL}
								isMobile={isMobile}
								width="100%"
								height="100%"
								enableHover={false}
								playOnHover={false}
								borderRadius={0}
							/>
						</Box>
					</Box>
				)}
				
				<GradientButton
					angle={90}
					colors={[colors.inkGray[9], colors.inkGray[7], colors.inkGray[9]]}
					size="large"
					onClick={handleJoinClick}
					fullWidth={false}
					animationDuration="4s"
					sx={{
						fontWeight: 600,
						textTransform: "none",
						width: "fit-content",
					}}
                >
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<DiscordIcon size={20} color="white" />
                        {t("discordBanner.button")}
					</Box>
				</GradientButton>
			</Stack>

			{/* 비디오 영역 컨테이너 - 데스크톱에서만 보임 */}
			{!isMobile && (
				<Box
					sx={{
						width: "240px",
						height: "240px",
						flexShrink: 0,
					}}
				>
					<Box
						sx={{
							width: "100%",
							height: "100%",
							transform: "scale(1)",
							transformOrigin: "center",
						}}
					>
						<LoopVideoBlock
							videoUrl={COMMUNITY_BANNER_VIDEO_URL}
							isMobile={isMobile}
							width="100%"
							height="100%"
							enableHover={false}
							playOnHover={false}
							borderRadius={0}
						/>
					</Box>
				</Box>
			)}
		</Stack>
	);
};

export default DiscordCommunityBanner;
